import React from 'react';

const CHANNEL_URL = 'https://www.youtube.com/@CoachGinge';

const post = {
  slug: 'coach-ginge',
  title: 'Meet Coach Ginge, Your Purrsonal Trainer',
  date: '2026-09-06',
  cover: '/blog/coach-ginge/20250614-202053.jpg',
  coverAlt: 'Grandad, a ginger-and-white Persian cat, giving the camera his trademark unimpressed look',
  coverPosition: 'center 58%',
  excerpt:
    'How a ginger-and-white Persian named Grandad became Coach Ginge, an AI-animated purrsonal trainer keeping his memory alive one workout at a time.',
  content: (
    <>
      <p>
        On March 21st 2026 we said goodbye to Grandad, our ginger-and-white
        Persian cat. He was the resident supervisor of the house and the least
        qualified but most dedicated personal trainer we have ever known. This
        is the story of how he became <a href={CHANNEL_URL}>Coach Ginge</a>, and
        why a small corner of YouTube is now run by an animated cat.
      </p>

      <h2>The coach behind the coach</h2>
      <p>
        Grandad earned his name honestly. He had the flat, unimpressed face of a
        cat who had seen it all and approved of very little: a permanent
        expression somewhere between a disappointed grandfather and a bouncer at
        a very exclusive lounge. Underneath the grump, he was a constant
        presence. Wherever you were, he was usually nearby, keeping an eye on
        things.
      </p>
      <p>
        His favourite job, self-appointed of course, was workout companion.
        Whenever a mat hit the floor, Grandad would appear and get involved.
        Hold a pose with a cat-sized gap underneath and he would stroll in and
        brush against you. Do anything standing and he would weave between your
        legs. During mat work he would plant himself in the middle of the
        action, at least until the novelty wore off and he retired to the edge
        of the mat to supervise. He never once corrected our form, but you could
        feel the judgement.
      </p>
      <p>
        What made Grandad Grandad was his constant need to be involved in
        absolutely everything. He always wanted to be present, always
        supervising, always part of the fun. Being part of the fun, in his book,
        meant coming over to inspect whatever was happening and then lazing
        right next to you while it carried on. It was how he got his name: he
        rarely moved more than a few steps without plonking himself back down to
        observe.
      </p>

      <div className="post-photo-grid">
        <img
          src="/blog/coach-ginge/20250412-161522.jpg"
          alt="Grandad sitting among mint plants on a sunny balcony"
        />
        <img
          src="/blog/coach-ginge/20250418-175516.jpg"
          alt="Grandad perched on top of the bin, freshly groomed"
        />
      </div>

      <div className="post-photo-grid">
        <img
          src="/blog/coach-ginge/20250201-000001.jpg"
          alt="Grandad sitting upright on a sofa like a small person"
        />
        <img
          src="/blog/coach-ginge/20250520-214035.jpg"
          alt="Grandad sprawled asleep on a grey sofa under a mustard blanket"
        />
      </div>

      <h2>From grief to reps</h2>
      <p>
        After he passed, the house got very quiet in a very specific way. He had
        got sick quickly, his kidneys failed, and when he was gone we were
        devastated. There was a Grandad-shaped gap at the edge of the mat, and a
        much bigger one everywhere else. We found ourselves wanting to remember
        him in every way we could: photos around the house, talking about him,
        any excuse to keep him close.
      </p>
      <p>
        Out of that came one idea we kept circling. Grandad&apos;s whole thing
        was showing up and being there while you did the work. What if he could
        keep doing that for other people too, anyone who could use a friendly
        nudge to move?
      </p>
      <p>
        That idea became <a href={CHANNEL_URL}>Coach Ginge, Your Purrsonal
        Trainer</a>: an AI-animated ginger cat who leads short, friendly,
        no-equipment workouts on YouTube. Every video is a tribute to him.
      </p>

      <div className="post-photo-grid">
        <img
          src="/blog/coach-ginge/20250514-123946.jpg"
          alt="Grandad sunbathing on the balcony"
        />
        <img
          src="/blog/coach-ginge/20250201-000002.jpg"
          alt="A selfie with Grandad looking thoroughly unimpressed"
        />
      </div>

      <h2>How the channel works</h2>
      <p>
        Coach Ginge is fully AI-animated. He is a ginger cat with Grandad&apos;s
        colouring and, we like to think, a bit of his expression. Every video is
        a follow-along session. No equipment, no gym, no experience needed. You
        press play and do what the cat does. The workouts are built for all
        fitness levels, and breaks are encouraged. Grandad usually took one with
        you.
      </p>

      {/* TODO: add a still of the animated Coach Ginge for contrast:
          <img src="/blog/coach-ginge/animated-ginge-1.png" alt="Coach Ginge, the AI-animated ginger cat, mid-workout" /> */}

      <div className="video-embed">
        <iframe
          src="https://www.youtube.com/embed/HS-5Mw-gYzA"
          title="Coach Ginge workout video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>

      <p>
        There are longer sessions for days when you want a proper workout, and
        shorts for a quick reset between meetings.
      </p>

      <div className="video-embed">
        <iframe
          src="https://www.youtube.com/embed/FIp2nzv-dFw"
          title="Coach Ginge short workout"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>

      <h2>Roll out your mat</h2>
      <p>
        Grandad spent years showing up to our workouts and never asked for
        anything except a warm spot to lie in. Coach Ginge is our way of paying
        that forward: a little bit of him, keeping other people company while
        they move.
      </p>

      <figure className="post-figure">
        <img
          src="/blog/coach-ginge/20250417-121409.jpg"
          alt="Grandad sitting on a granite wall outdoors, looking unimpressed"
        />
      </figure>

      <p>
        If that sounds like a trainer you could work with,{' '}
        <a href={CHANNEL_URL}>subscribe to Coach Ginge on YouTube</a> and try a
        session. Go on, roll out your mat. The cat is watching.
      </p>

      <div className="video-embed">
        <iframe
          src="https://www.youtube.com/embed/wx5ic3lKCM4"
          title="Coach Ginge workout video"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      </div>

      <p className="post-signoff">
        For Grandad, the best supervisor a workout ever had.
      </p>
    </>
  ),
};

export default post;
